// loadWebshopConfig — the big settings→state load effect body — extracted verbatim from WebshopConfigScreen.js. The screen passes
// its state setters/refs/props in one ctx object; closure semantics are
// identical to the inline version (ctx is rebuilt per call, so every value is
// as current as the render that triggered it).

import {
    CADEAUBON_CONFIG_DEFAULT, BRANDS_CONFIG_DEFAULT, OVERIGE_CONFIG_DEFAULT,
    STL_CONFIG_DEFAULT, DEFAULT_CAROUSEL_CONFIG, DEFAULT_IMAGE_GRID_CONFIG,
    PROCESSING_CONFIG_DEFAULT, SYNC_CONFIG_DEFAULT,
} from './configDefaults';
import i18n from '../../../i18n';
import SftGlobalData from '../../../library/SftGlobalData';
import { parseLangCsv } from '../WebshopLanguagesContext';
import { detectHoursSeparator, storeRowToForm } from '../data/stores';
import { parseCsvToChips, parseTemplateToChips } from '../style/cards/ProductTitleAndUrlCard';
import { DEFAULT_GROUPS_FILTER_CONFIG } from '../style/modals/GroupsFilterModal';
import { DEFAULT_PRODUCT_CARD_CONFIG } from '../style/modals/ProductCardModal';
import { DEFAULT_PRODUCT_GRID_CONFIG } from '../style/modals/ProductGridModal';
import { BRANDS_STYLE_TO_LAYOUT, CARD_CONTENT, DEFAULT_CARDS, LAYOUT_TO_OVERIGE_STYLE, STATIC_BLOCKS, bannerRowToForm, containerRowToFormData, customerGroupRowToForm, dynRowToPage, layoutRowToForm, placeholderRowToForm, readBool, readNum, readStr, settingsToCadeaubon, settingsToProcessing, settingsToSync, shopImageBase, slideRowToForm, gridCellRowToForm, rowsByLang, contentsRowToPage, contentsRowToColumn, linkRowToFooter, linkRowToSocial } from './configHelpers';

export const loadWebshopConfig = (ctx) => {
    const {
        DEFAULT_EXTRA_FOOTER_CONFIG,
        DEFAULT_FOOTER_CONFIG,
        DEFAULT_GLOBAL_WIDTHS,
        DEFAULT_HEADER_LOGO_CONFIG,
        DEFAULT_IMAGE_GALLERY_CONFIG,
        DEFAULT_NAVIGATION_CONFIG,
        DEFAULT_PRODUCT_DETAIL_INFO_CONFIG,
        carouselImgBaseRef,
        gridImgBaseRef,
        layoutImgBaseRef,
        looksCtaIdRef,
        navbarOrderLevelRef,
        originalBannersRef,
        originalBrandsConfigRef,
        originalBrandsRawJsonRef,
        originalCadeaubonConfigRef,
        originalCadeaubonLayoutsRef,
        originalCardsRef,
        originalCarouselConfigRef,
        originalContainerIdsRef,
        originalCustomerGroupsRef,
        originalDynamicPagesRef,
        originalExtraFooterConfigRef,
        originalFooterConfigRef,
        originalGlobalWidthsRef,
        originalGroupsFilterConfigRef,
        originalHeaderLogoConfigRef,
        originalHoursSeparatorRef,
        originalImageGalleryConfigRef,
        originalImageGridConfigRef,
        originalLooksConfigRef,
        originalMapSettingsRef,
        originalNavbarOrderRef,
        originalNavigationConfigRef,
        originalOverigeConfigRef,
        originalPlaceholdersRef,
        originalProcChequeRef,
        originalProcessingConfigRef,
        originalProductCardConfigRef,
        originalProductDetailInfoConfigRef,
        originalProductGridConfigRef,
        originalStaticPagesRef,
        originalStoresRef,
        originalStyleSettingsRef,
        originalSyncConfigRef,
        originalTicketModuleRef,
        productHoverMaxWidthsRef,
        props,
        setActive,
        setActive404,
        setAvailable,
        setAvailable404,
        setBanners,
        setBrandsConfig,
        setCadeaubonConfig,
        setCadeaubonLayouts,
        setCadeaubonPlaceholders,
        setCards,
        setCarouselConfig,
        setConfigError,
        setConfigLoading,
        setContainersDirty,
        setCountryOptions,
        setCustomerCategoryGroups,
        setDynamicPages,
        setExtraFooterConfig,
        setFooterConfig,
        setGlobalWidths,
        setGmapsApiKey,
        setGroupsFilterConfig,
        setHeaderLogoConfig,
        setHomeSectionsOrder,
        setHoursSeparator,
        setImageGalleryConfig,
        setImageGridConfig,
        setLooksConfig,
        setMapSettings,
        setNavFilterStyle,
        setNavbarOrder,
        setNavigationConfig,
        setOverigeConfig,
        setPage404SectionsOrder,
        setPaymentProviderAvailability,
        setPaymentTypeRows,
        setPosOptions,
        setProcessingConfig,
        setProductCardConfig,
        setProductDetailInfoConfig,
        setProductGridConfig,
        setSalutationOptions,
        setShopFieldLabels,
        setStaticPages,
        setStores,
        setStyleSettings,
        setSyncConfig,
        setTextTypeOptions,
        setWarehouseOptions,
    } = ctx;
        const webshopId = props.selectedWebshop?.id;
        if (!webshopId || !props.api) return;

        let cancelled = false;
        (async () => {
            setConfigLoading(true);
            setConfigError(null);
            try {
                const lang = (props.lang || 'nl').toLowerCase();
                // Parallel fetch: storefront settings (via the webshop proxy)
                // AND the customer's kasvars (via auth/sftData). The kasvars
                // call returns labels for category_1..7 (vars Code1Oms..
                // Code7Oms); we use those as the per-shop labels on the
                // detail-page cards. If the kasvars call fails the cards
                // fall back to the generic cfg_pdi_details_category_N i18n.
                // The old kitchen-sink GET /admin/settings is split into four
                // focused bundles fetched IN PARALLEL; the dump-shaped `s`
                // object is reassembled below so none of the field mappings
                // change. The legacy endpoint still exists as a fallback.
                const bundleGet = (path) => SftGlobalData.post('webshop&action=proxy', {
                    key:        props.api,
                    webshop_id: webshopId,
                    sftUserId:  props.login?.sft_user_id,
                    user:       props.login?.login,
                    method:     'GET',
                    path,
                }).catch(() => null);
                const [coreResult, assetsResult, contentsResult, collectionsResult, kasvarsResult, storesResult, dynPagesResult, localizationResult, placeholdersResult, posResult, warehouseResult, customerCodesResult, countriesResult, layoutsResult, textTypesResult] = await Promise.all([
                    bundleGet('/admin/settings-core'),
                    bundleGet('/admin/settings-assets'),
                    bundleGet('/admin/settings-contents'),
                    bundleGet('/admin/settings-collections'),
                    SftGlobalData.post('sftData', {
                        key:  props.api,
                        type: 'kasvars',
                        code: [
                            'Code1Oms','Code2Oms','Code3Oms','Code4Oms','Code5Oms','Code6Oms','Code7Oms',
                            'Levart1Oms','Levart2Oms','Levart3Oms','Levart4Oms','Levart5Oms',
                            // POS payment-means labels for the Verwerking payment-type dropdown.
                            'kas_betaal1','kas_betaal2','kas_betaal3','kas_betaal4','kas_betaal5',
                            'kas_betaal6','kas_betaal7','kas_betaal8','kas_betaal9',
                        ],
                    }).catch(() => null),
                    // Stores list — all non-deleted rows (no render=list/visible
                    // filter, so the admin sees invisible stores too).
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/stores',
                    }).catch(() => null),
                    // Dynamic pages (custom /extra/* pages).
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/dynamic-pages',
                    }).catch(() => null),
                    // i18n strings — used for the look CTA label (GLOBAL.LOOKS_DETAILS).
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/localization',
                    }).catch(() => null),
                    // Cheque (gift-voucher) placeholders — the Cadeaubon Translations table.
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/cheque-placeholders',
                    }).catch(() => null),
                    // POS list (auth backend) — for the Verwerking "register under POS" dropdown.
                    SftGlobalData.post('sftData', {
                        key:  props.api,
                        type: 'pos',
                    }).catch(() => null),
                    // Store/warehouse list (auth backend) — for the Verwerking "order warehouse" dropdown.
                    SftGlobalData.post('sftData', {
                        key:  props.api,
                        type: 'stores',
                    }).catch(() => null),
                    // Customer codes (kl_codes) — group 90 = salutations for the Verwerking dropdown.
                    SftGlobalData.post('sftData', {
                        key:  props.api,
                        type: 'customerCodes',
                    }).catch(() => null),
                    // Webshop countries (managed in the Data tab) — visible ones feed
                    // the Verwerking "default country" dropdown.
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/countries',
                    }).catch(() => null),
                    // Cheque/voucher layouts — the Cadeaubon "Voucher layouts" tab.
                    SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/cheques-layouts',
                    }).catch(() => null),
                    // POS article text types — readable labels for the
                    // description-texts card's type codes. [] on any failure.
                    bundleGet('/admin/text-types'),
                ]);
                if (cancelled) return;
                // Per-bundle resilience: use every bundle that loaded, and only
                // fall back to the legacy kitchen-sink call for the parts that
                // failed. A single failing bundle (e.g. collections 500) must
                // not blank the whole screen — core (logo/colours) still works.
                const allBundlesOk = coreResult?.success && assetsResult?.success
                    && contentsResult?.success && collectionsResult?.success;
                let s = {
                    ...(coreResult?.success        ? (coreResult.data        || {}) : {}),
                    ...(assetsResult?.success      ? (assetsResult.data      || {}) : {}),
                    ...(contentsResult?.success    ? (contentsResult.data    || {}) : {}),
                    ...(collectionsResult?.success ? (collectionsResult.data || {}) : {}),
                };
                if (!allBundlesOk) {
                    // Pull the legacy dump as a base for whatever is missing,
                    // then overlay the fresh bundles that DID succeed.
                    const legacy = await SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/admin/settings',
                    }).catch(() => null);
                    if (cancelled) return;
                    if (legacy?.success && legacy.data) {
                        s = { ...legacy.data, ...s };
                    } else if (Object.keys(s).length === 0) {
                        const failed = legacy || coreResult || assetsResult || contentsResult || collectionsResult;
                        setConfigError({
                            message: failed?.message || 'Failed to load settings',
                            code:    failed?.translation_key,
                        });
                        return;
                    }
                    // else: some bundles loaded — degrade gracefully with those.
                }

                // shopFieldLabels — `{var, value}` rows from kasvars.
                //   "CodeNOms"   → "category_N"   (1..7)
                //   "LevartNOms" → "detail_N"     (1..5)
                const kasvarRows = (kasvarsResult?.data?.data && Array.isArray(kasvarsResult.data.data))
                    ? kasvarsResult.data.data
                    : [];
                const nextFieldLabels = {};
                const payTypeRows = [];
                for (const row of kasvarRows) {
                    const v = String(row?.var   || '');
                    const value = row?.value;
                    if (!value) continue;
                    let m;
                    if ((m = /^Code([1-7])Oms$/i.exec(v))) {
                        nextFieldLabels[`category_${m[1]}`] = String(value);
                    } else if ((m = /^Levart([1-5])Oms$/i.exec(v))) {
                        nextFieldLabels[`detail_${m[1]}`] = String(value);
                    } else if ((m = /^kas_betaal([1-9])$/i.exec(v))) {
                        // Keep the kasvar.code so the dropdown can pick the row matching
                        // the receipt POS id (kassa → winkel → globaal fallback).
                        payTypeRows.push({ n: Number(m[1]), value: String(value), code: row?.code });
                    }
                }
                setShopFieldLabels(nextFieldLabels);
                // Payment types — raw rows; the POS-id filtering happens in a memo.
                setPaymentTypeRows(payTypeRows);

                // Stores — map the mini-api rows to the admin editor shape.
                const storeRows = (storesResult?.success && Array.isArray(storesResult.data))
                    ? storesResult.data
                    : [];
                const loadedStores = storeRows.map(storeRowToForm);
                setStores(loadedStores);
                originalStoresRef.current = loadedStores;
                const detectedSep = detectHoursSeparator(storeRows);
                setHoursSeparator(detectedSep);
                originalHoursSeparatorRef.current = detectedSep;
                setGmapsApiKey(s.GOOGLE_MAPS_TOKEN || s.GMAPS_API_KEY || '');

                // Store-locations map settings (ABOUT_US_MAP_* + mapStyle.json).
                const loadedMap = {
                    multi:    !!Number(s.ABOUT_US_MAP_MULTI),
                    position: s.ABOUT_US_MAP_POSITION || 'bottom',
                    zoom:     s.ABOUT_US_MAP_ZOOM     != null && s.ABOUT_US_MAP_ZOOM     !== '' ? Number(s.ABOUT_US_MAP_ZOOM)     : 14,
                    zoomMax:  s.ABOUT_US_MAP_ZOOM_MAX != null && s.ABOUT_US_MAP_ZOOM_MAX !== '' ? Number(s.ABOUT_US_MAP_ZOOM_MAX) : 16,
                    style:    (s.mapStyle != null ? String(s.mapStyle) : '').trim() || '[]',
                };
                setMapSettings(loadedMap);
                originalMapSettingsRef.current = loadedMap;

                // Cart-page account cards ← contents rows (wysiwyg only). Keep
                // the static label/id from DEFAULT_CARDS, override the body +
                // iframe flag from the backend content row.
                const loadedCards = DEFAULT_CARDS.map(base => {
                    const meta = CARD_CONTENT[base.id];
                    const row  = meta ? s[meta.field] : null;
                    if (!meta) return base;
                    return {
                        ...base,
                        useIframe: !!(row && row.use_iframe),
                        contentNL: (row && row.body_nl) || '',
                        contentEN: (row && row.body_en) || '',
                        contentFR: (row && row.body_fr) || '',
                    };
                });
                setCards(loadedCards);
                originalCardsRef.current = loadedCards;

                // Brands page (Merken). Flat keys + the BRANDS_PAGE_CONFIG blob.
                const rawBpc = (s.BRANDS_PAGE_CONFIG && typeof s.BRANDS_PAGE_CONFIG === 'object') ? s.BRANDS_PAGE_CONFIG : {};
                originalBrandsRawJsonRef.current = rawBpc;
                const D = BRANDS_CONFIG_DEFAULT;
                const styleFromShort = (o, def) => ({
                    size:      o && o.s != null ? Number(o.s) : def.size,
                    color:     o && o.c ? o.c : def.color,
                    bold:      o ? !!o.b : def.bold,
                    italic:    o ? !!o.i : def.italic,
                    underline: o ? !!o.u : def.underline,
                });
                const rd = rawBpc.d || {}, rm = rawBpc.m || {};
                const loadedBrands = {
                    whatToShow: {
                        onlyOnline: !!s.BRANDS_ONLY_ONLINE,
                        navigationCategory: (s.BRANDS_CATEGORY_FILTER && s.BRANDS_CATEGORY_FILTER !== 'none') ? String(s.BRANDS_CATEGORY_FILTER) : '',
                    },
                    layout: BRANDS_STYLE_TO_LAYOUT[s.BRANDS_PAGE_STYLE] || 'grouped',
                    settings: {
                        displayContinuous:        rawBpc.lm || D.settings.displayContinuous,
                        displayGrouped:           rawBpc.am || D.settings.displayGrouped,
                        showLettersWithoutBrands: !!rawBpc.sul,
                        letterColumns:            rawBpc.cols != null ? Number(rawBpc.cols) : D.settings.letterColumns,
                        brandsPerLetter:          rawBpc.max  != null ? Number(rawBpc.max)  : D.settings.brandsPerLetter,
                    },
                    textStyles: {
                        desktop: {
                            brandName:     styleFromShort(rd.bn, D.textStyles.desktop.brandName),
                            alphabetNav:   styleFromShort(rd.ai, D.textStyles.desktop.alphabetNav),
                            letterHeading: styleFromShort(rd.al, D.textStyles.desktop.letterHeading),
                        },
                        mobile: {
                            brandName:     styleFromShort(rm.bn, D.textStyles.mobile.brandName),
                            alphabetNav:   styleFromShort(rm.ai, D.textStyles.mobile.alphabetNav),
                            letterHeading: styleFromShort(rm.al, D.textStyles.mobile.letterHeading),
                        },
                    },
                };
                setBrandsConfig(loadedBrands);
                originalBrandsConfigRef.current = loadedBrands;

                // Dynamic pages.
                const dynRows = (dynPagesResult?.success && Array.isArray(dynPagesResult.data)) ? dynPagesResult.data : [];
                const loadedDynPages = dynRows.map(dynRowToPage);
                setDynamicPages(loadedDynPages);
                originalDynamicPagesRef.current = loadedDynPages;

                // Information banners — comes embedded in the settings dump.
                const bannerRows = Array.isArray(s.informationBanners) ? s.informationBanners : [];
                const loadedBanners = bannerRows.map(bannerRowToForm);
                setBanners(loadedBanners);
                originalBannersRef.current = loadedBanners;

                // Shop the look — intro from TEXT_LOOKS content row, rest from
                // LOOKS_*/LOOK_*/SHOW_FIELDS_ON_LOOK_* settings.
                const looksRow = s.contents_looks || null;
                const DL = STL_CONFIG_DEFAULT;
                const toFieldArr = (v) => String(v || '').split(/\s+/).map(x => x.trim()).filter(Boolean);
                // Look CTA label — the GLOBAL.LOOKS_DETAILS i18n row.
                const i18nRows = (localizationResult?.success && Array.isArray(localizationResult.data)) ? localizationResult.data : [];
                const ctaRow = i18nRows.find(r => String(r.group) === 'GLOBAL' && String(r.item) === 'LOOKS_DETAILS') || null;
                looksCtaIdRef.current = ctaRow ? ctaRow.id : null;
                const ctaText = ctaRow
                    ? { nl: ctaRow.nl || ctaRow.default || '', en: ctaRow.en || ctaRow.default || '', fr: ctaRow.fr || ctaRow.default || '' }
                    : { ...DL.overlay.ctaText };
                const loadedLooks = {
                    introText: {
                        nl: (looksRow && looksRow.body_nl) || '',
                        en: (looksRow && looksRow.body_en) || '',
                        fr: (looksRow && looksRow.body_fr) || '',
                    },
                    introEditor: 'visual',
                    looksGrid: {
                        navigationCategory: s.LOOKS_NAVIGATION_CODE != null && s.LOOKS_NAVIGATION_CODE !== '' ? String(s.LOOKS_NAVIGATION_CODE) : DL.looksGrid.navigationCategory,
                        displayOrder:       s.LOOKS_LIST_SORTING || DL.looksGrid.displayOrder,
                        looksPerRow:        s.MAX_COLUMNS_LOOKS != null && s.MAX_COLUMNS_LOOKS !== '' ? Number(s.MAX_COLUMNS_LOOKS) : DL.looksGrid.looksPerRow,
                        // The looks page renders its intro via <app-content-display>,
                        // which uses an iframe when the TEXT_LOOKS content row's
                        // use_iframe flag is set (same mechanism as About).
                        showInIframe:       !!(looksRow && Number(looksRow.use_iframe)),
                    },
                    overlay: {
                        onlyOnHover:          !!Number(s.LOOKS_OVERLAY_ONLY_ON_HOVER),
                        bandBg:               s.LOOKS_OVERLAY_BGC || DL.overlay.bandBg,
                        bandHeightPct:        s.LOOKS_OVERLAY_SIZE != null && s.LOOKS_OVERLAY_SIZE !== '' ? Number(s.LOOKS_OVERLAY_SIZE) : DL.overlay.bandHeightPct,
                        bandVerticalPosition: s.LOOKS_OVERLAY_POSITION || DL.overlay.bandVerticalPosition,
                        showTitle:            s.LOOKS_OVERLAY_SHOW_TITLE != null ? !!Number(s.LOOKS_OVERLAY_SHOW_TITLE) : DL.overlay.showTitle,
                        titleColor:           s.LOOKS_OVERLAY_FGC || DL.overlay.titleColor,
                        titleFontSize:        s.LOOKS_OVERLAY_TITLE_FS != null && s.LOOKS_OVERLAY_TITLE_FS !== '' ? Number(s.LOOKS_OVERLAY_TITLE_FS) : DL.overlay.titleFontSize,
                        showCta:              s.LOOKS_OVERLAY_SHOW_BUTTON != null ? !!Number(s.LOOKS_OVERLAY_SHOW_BUTTON) : DL.overlay.showCta,
                        ctaText:              ctaText,
                        ctaBg:                s.LOOKS_OVERLAY_BUTTON_BGC || DL.overlay.ctaBg,
                        ctaTextColor:         s.LOOKS_OVERLAY_BUTTON_FGC || DL.overlay.ctaTextColor,
                        ctaFontSize:          s.LOOKS_OVERLAY_BUTTON_FS != null && s.LOOKS_OVERLAY_BUTTON_FS !== '' ? Number(s.LOOKS_OVERLAY_BUTTON_FS) : DL.overlay.ctaFontSize,
                    },
                    detailPage: {
                        descriptionPosition:        s.LOOKS_DETAIL_TEXT_LOCATION || DL.detailPage.descriptionPosition,
                        showDividerBetweenProducts: !!Number(s.LOOK_SHOW_DIVIDER),
                        showOutOfStock:             !!Number(s.LOOK_SHOW_OUT_OF_STOCK),
                    },
                    productTitle:    toFieldArr(s.SHOW_FIELDS_ON_LOOK_TITLE),
                    productSubtitle: toFieldArr(s.SHOW_FIELDS_ON_LOOK_SUBHEADING),
                };
                setLooksConfig(loadedLooks);
                originalLooksConfigRef.current = loadedLooks;

                // Overige — custom code from the script-include files, the
                // GTM/Meta-Pixel tools from their settings, and the cookie
                // banner from cookie.js (s.cookie_consent). GA4/TikTok/Hotjar +
                // the per-tool active flag stay pending.
                const trackingTools = [];
                if (s.GTM_ID)   trackingTools.push({ key: 'gtm',       id: String(s.GTM_ID),   active: true });
                if (s.PIXEL_ID) trackingTools.push({ key: 'metaPixel', id: String(s.PIXEL_ID), active: true });
                // Cookie consent (layout cloud/box/bar ↔ our floating/modal/bottom).
                const cc       = (s.cookie_consent && typeof s.cookie_consent === 'object') ? s.cookie_consent : null;
                const ccModal  = cc?.guiOptions?.consentModal || {};
                const ccCats   = cc?.categories || {};
                const ccStyle  = LAYOUT_TO_OVERIGE_STYLE[ccModal.layout] || OVERIGE_CONFIG_DEFAULT.cookies.bannerStyle;
                const loadedCookies = {
                    categories: {
                        necessary: true,
                        analytics: Object.prototype.hasOwnProperty.call(ccCats, 'analytics'),
                        marketing: Object.prototype.hasOwnProperty.call(ccCats, 'marketing'),
                    },
                    bannerStyle: ccStyle,
                    position:    ccModal.position || OVERIGE_CONFIG_DEFAULT.cookies.position,
                };
                const loadedOverige = {
                    ...OVERIGE_CONFIG_DEFAULT,
                    cookies: loadedCookies,
                    trackingTools,
                    customCode: {
                        ...OVERIGE_CONFIG_DEFAULT.customCode,
                        head: s.scriptsHeader || '',
                        body: s.scriptsBody || '',
                    },
                };
                setOverigeConfig(loadedOverige);
                originalOverigeConfigRef.current = loadedOverige;

                // Cadeaubon — settings from the CHEQUES_* keys in the dump,
                // translations from the separate /cheque-placeholders list.
                const loadedCadeaubon = settingsToCadeaubon(s, CADEAUBON_CONFIG_DEFAULT);
                setCadeaubonConfig(loadedCadeaubon);
                originalCadeaubonConfigRef.current = loadedCadeaubon;

                const placeholderRows = (placeholdersResult?.success && Array.isArray(placeholdersResult.data)) ? placeholdersResult.data : [];
                const loadedPlaceholders = placeholderRows.map(placeholderRowToForm);
                setCadeaubonPlaceholders(loadedPlaceholders);
                originalPlaceholdersRef.current = loadedPlaceholders;

                // Cadeaubon voucher layouts (GET /cheques-layouts). Preview images
                // live under <img_path>/cheque-layouts/; prefix with the webshop
                // base so they resolve in the admin app.
                // One base URL for every image-prefix below (F21) — it was
                // computed three times under three names, a real divergence risk.
                const shopBase = shopImageBase(props.selectedWebshop?.display_name);
                const layoutImgBase = `${shopBase}${s.img_path || ''}/cheque-layouts/`;
                layoutImgBaseRef.current = layoutImgBase;
                const layoutRows = (layoutsResult?.success && Array.isArray(layoutsResult.data)) ? layoutsResult.data : [];
                const loadedLayouts = layoutRows.map(r => layoutRowToForm(r, layoutImgBase));
                setCadeaubonLayouts(loadedLayouts);
                originalCadeaubonLayoutsRef.current = loadedLayouts;

                // POS text-type labels for the description-texts card ([{id, name}]).
                setTextTypeOptions((textTypesResult?.success && Array.isArray(textTypesResult.data)) ? textTypesResult.data : []);

                // Verwerking — settings from the dump (incl. the 3 CHEQUES_* JSON
                // discount objects, kept raw for round-tripping unknown codes).
                const loadedProcessing = settingsToProcessing(s, PROCESSING_CONFIG_DEFAULT);
                setProcessingConfig(loadedProcessing);
                originalProcessingConfigRef.current = loadedProcessing;
                originalTicketModuleRef.current = String(s.PROCESS_TICKET_MODULE || '').toUpperCase();
                // Online payment provider availability (disables providers without
                // credentials). Only MultiSafePay is offered now.
                setPaymentProviderAvailability({ msp: !!(s.MSP_API_TOKEN || s.MSP_API_TOKEN_TEST) });
                originalProcChequeRef.current = {
                    allowed:      (s.CHEQUES_ALLOWED        && typeof s.CHEQUES_ALLOWED        === 'object') ? s.CHEQUES_ALLOWED        : {},
                    asPayment:    (s.CHEQUES_AS_PAYMENT     && typeof s.CHEQUES_AS_PAYMENT     === 'object') ? s.CHEQUES_AS_PAYMENT     : {},
                    addToReceipt: (s.CHEQUES_ADD_TO_RECEIPT && typeof s.CHEQUES_ADD_TO_RECEIPT === 'object') ? s.CHEQUES_ADD_TO_RECEIPT : {},
                };

                // Custom customer fields (customers_categories_groups, from the dump).
                const ccgRows = Array.isArray(s.customerCategoryGroups) ? s.customerCategoryGroups : [];
                const loadedCustomerGroups = ccgRows.map(customerGroupRowToForm);
                setCustomerCategoryGroups(loadedCustomerGroups);
                originalCustomerGroupsRef.current = loadedCustomerGroups;

                // POS list (auth backend) → [{ value: code, label: "code — oms" }].
                const posRows = (posResult?.data?.data && Array.isArray(posResult.data.data)) ? posResult.data.data : [];
                setPosOptions(posRows
                    .filter(r => r && r.code != null && String(r.code) !== '')
                    .map(r => ({ value: String(r.code), label: r.oms ? `${r.code} — ${r.oms}` : String(r.code) })));

                // Store/warehouse list (auth backend) → same shape.
                const warehouseRows = (warehouseResult?.data?.data && Array.isArray(warehouseResult.data.data)) ? warehouseResult.data.data : [];
                const warehouseCodes = warehouseRows
                    .filter(r => r && r.code != null && String(r.code) !== '')
                    .map(r => String(r.code));
                setWarehouseOptions(warehouseRows
                    .filter(r => r && r.code != null && String(r.code) !== '')
                    .map(r => ({ value: String(r.code), label: r.oms ? `${r.code} — ${r.oms}` : String(r.code) })));

                // Synchronisatie — scalar settings from the dump. FETCH_FOR_STORES
                // '00'/'' expands to every store code (the universe just built).
                const loadedSync = settingsToSync(s, SYNC_CONFIG_DEFAULT, warehouseCodes);
                setSyncConfig(loadedSync);
                originalSyncConfigRef.current = loadedSync;

                // Salutations = customer codes (kl_codes) in group 90 → [{ value: totcode, label: "totcode — codeoms" }].
                const custCodeRows = (customerCodesResult?.data?.data && Array.isArray(customerCodesResult.data.data)) ? customerCodesResult.data.data : [];
                setSalutationOptions(custCodeRows
                    .filter(r => r && Number(r.codegroep) === 90 && r.totcode != null && String(r.totcode) !== '')
                    .map(r => {
                        const oms = String(r.codeoms || r.oms || '').trim();
                        return { value: String(r.totcode), label: oms ? `${r.totcode} — ${oms}` : String(r.totcode) };
                    }));

                // Countries (webshop DB, managed in the Data tab). The default-country
                // dropdown mirrors the old admin: ALL countries, prefered first (the
                // mini-api already orders prefered DESC, name). Value = iso, label
                // localized to the admin language.
                const countryRows = (countriesResult?.success && Array.isArray(countriesResult.data)) ? countriesResult.data : [];
                const adminLang = String(i18n.language || 'nl').slice(0, 2).toLowerCase();
                const locKey = ['nl', 'en', 'fr'].includes(adminLang) ? `loc_${adminLang}` : 'loc_nl';
                setCountryOptions(countryRows
                    .filter(r => r && r.iso != null && String(r.iso) !== '')
                    .map(r => ({ value: String(r.iso), label: String(r[locKey] || r.loc_nl || r.name || r.iso) })));

                const containers = Array.isArray(s.productContainers) ? s.productContainers : [];
                const orderStr = s.HOME_SECTIONS_ORDER || '';
                const order = orderStr.split(',').map(x => x.trim()).filter(Boolean);

                originalContainerIdsRef.current = new Set(containers.map(c => `pd_cnt-${c.id}`));
                setContainersDirty(false);
                setHomeSectionsOrder(orderStr);

                // Build the block catalog: known statics + fetched product containers.
                // Display name uses the localized field for the MySft user's
                // current language, falling back to the other locales then to
                // the admin-internal `reference`. (`lang` is declared at the
                // top of the try block.)
                // Reference-first (stored in DB `description`; matches blockLabel)
                // so a container's list label is stable across save/reload.
                const localized = (c) =>
                    c.description || c[`loc_${lang}`] || c.loc_nl || c.loc_en || c.loc_fr || `Container ${c.id}`;
                const containerBlocks = containers.map(c => ({
                    id:   `pd_cnt-${c.id}`,
                    name: localized(c),
                    isContainer: true,
                    configurable: true,
                    container: c,                          // raw DB row (for save flow later)
                    config: containerRowToFormData(c),     // pre-mapped to the modal's form shape
                }));
                const allBlocks = [...STATIC_BLOCKS, ...containerBlocks];
                const byId = Object.fromEntries(allBlocks.map(b => [b.id, b]));

                // Active = HOME_SECTIONS_ORDER, in order. Unknown ids (typically
                // stale references to deleted product containers) are dropped
                // silently here — the next save will rewrite HOME_SECTIONS_ORDER
                // without them, cleaning up the legacy data.
                const activeList = order
                    .map(id => byId[id])
                    .filter(Boolean);
                const placedIds = new Set(activeList.map(b => b.id));
                const availableList = allBlocks.filter(b => (!b.onlyOn || b.onlyOn === 'home') && !placedIds.has(b.id));

                setActive(activeList);
                setAvailable(availableList);

                // 404 page: its own active + available lists, independent of the
                // Front page (landing). Active comes from PAGE_404_SECTIONS_ORDER
                // (default 'text'); available = all 404-allowed blocks (no onlyOn,
                // or onlyOn==='page404') not already placed.
                const order404Str  = s.PAGE_404_SECTIONS_ORDER || '';
                const order404      = order404Str.split(',').map(x => x.trim()).filter(Boolean);
                const active404List = order404.map(id => byId[id]).filter(Boolean);
                const placed404     = new Set(active404List.map(b => b.id));
                const available404List = allBlocks.filter(b => (!b.onlyOn || b.onlyOn === 'page404') && !placed404.has(b.id));
                setPage404SectionsOrder(order404Str);
                setActive404(active404List);
                setAvailable404(available404List);

                // ---- Map legacy colour settings into styleSettings ----------
                // Pulls the storefront's saved colour values into the Style tab
                // so both the mockup and the ColorField inputs reflect what's
                // actually live on the shop. Falls back to the existing default
                // if a key is missing or empty.
                const hex = (v, fallback) => {
                    if (v == null || v === '') return fallback;
                    const str = String(v).trim();
                    // Expand short hex (#fff → #ffffff) so <input type="color"> accepts it.
                    if (/^#[0-9a-f]{3}$/i.test(str)) {
                        return '#' + str[1] + str[1] + str[2] + str[2] + str[3] + str[3];
                    }
                    return str;
                };
                // Font: Google fonts store the name in FONT_FAMILY_GOOGLE, system
                // stacks the full CSS stack in FONT_FAMILY. Surface whichever is
                // active as the dropdown value.
                const useGoogleFont = readBool(s.FONT_FAMILY_USE_GOOGLE, false);
                const loadedFont = useGoogleFont
                    ? (s.FONT_FAMILY_GOOGLE || s.FONT_FAMILY || 'Roboto')
                    : (s.FONT_FAMILY || 'Roboto');
                // Back-to-top: backend CIRCLE_LEFT/MIDDLE/RIGHT ↔ left/center/right.
                const backToTopFromBackend = (v) => {
                    const u = String(v || '').toUpperCase();
                    if (u.includes('LEFT'))   return 'left';
                    if (u.includes('MIDDLE') || u.includes('CENTER')) return 'center';
                    return 'right';
                };
                setStyleSettings(prev => {
                    const next = ({
                    ...prev,
                    global: {
                        ...prev.global,
                        background: hex(s.COLOR_BODY_BACKGROUND, prev.global.background),
                        text:       hex(s.COLOR_BODY_TEXT,       prev.global.text),
                        primary:    hex(s.COLOR_PRIMARY,         prev.global.primary),
                        font:       loadedFont,
                    },
                    visibility: {
                        ...prev.visibility,
                        languageBar:       readBool(s.SHOW_LANGUAGE_BAR, prev.visibility.languageBar),
                        footer:            readBool(s.SHOW_FOOTER,       prev.visibility.footer),
                        backToTopPosition: backToTopFromBackend(s.BACK_TO_TOP_BUTTON),
                        // Storefront-active languages; admin-available defaults to
                        // the active set (then all) when ADMIN_LANGUAGES is unset.
                        visibleLanguages:  parseLangCsv(s.VISIBILE_LANGUAGES) || prev.visibility.visibleLanguages,
                        adminLanguages:    parseLangCsv(s.ADMIN_LANGUAGES) || parseLangCsv(s.VISIBILE_LANGUAGES) || prev.visibility.adminLanguages,
                    },
                    sections: {
                        ...prev.sections,
                        language: {
                            ...prev.sections.language,
                            bgColor:     hex(s.COLOR_BACKGROUND_LANG,  prev.sections.language.bgColor),
                            textColor:   hex(s.COLOR_TEXT_LANG,        prev.sections.language.textColor),
                            activeColor: hex(s.COLOR_TEXT_ACTIVE_LANG, prev.sections.language.activeColor),
                        },
                        header: {
                            ...prev.sections.header,
                            bgColor:   hex(s.COLOR_BACKGROUND_HEADER, prev.sections.header.bgColor),
                            textColor: hex(s.COLOR_TEXT_HEADER,       prev.sections.header.textColor),
                        },
                        menu: {
                            ...prev.sections.menu,
                            // Navigation bar = SECONDARY pair. The cart-badge bubble
                            // has its own dedicated setting (COLOR_IN_CART_BACKGROUND)
                            // — distinct from COLOR_PRIMARY which now drives the
                            // Add-to-cart button + main price colour.
                            bgColor:     hex(s.COLOR_SECONDARY,          prev.sections.menu.bgColor),
                            textColor:   hex(s.COLOR_SECONDARY_INVERTED, prev.sections.menu.textColor),
                            accentColor: hex(s.COLOR_IN_CART_BACKGROUND, prev.sections.menu.accentColor),
                            // Custom hover colours (legacy: COLOR_USE_CUSTOM_HOVER gate +
                            // COLOR_PRIMARY_HOVER/_TEXT + COLOR_SECONDARY_HOVER/_TEXT).
                            useCustomHover:     readBool(s.COLOR_USE_CUSTOM_HOVER,   prev.sections.menu.useCustomHover),
                            primaryHover:       hex(s.COLOR_PRIMARY_HOVER,           prev.sections.menu.primaryHover),
                            primaryHoverText:   hex(s.COLOR_PRIMARY_HOVER_TEXT,      prev.sections.menu.primaryHoverText),
                            secondaryHover:     hex(s.COLOR_SECONDARY_HOVER,         prev.sections.menu.secondaryHover),
                            secondaryHoverText: hex(s.COLOR_SECONDARY_HOVER_TEXT,    prev.sections.menu.secondaryHoverText),
                        },
                        // Title / subtitle / description default to the global body
                        // text colour (`COLOR_BODY_TEXT`) so that changing the Global
                        // panel's "Text color" field is visibly reflected in the mockup.
                        // Per-section overrides via the cogwheel still win.
                        title: {
                            ...prev.sections.title,
                            textColor: hex(s.COLOR_BODY_TEXT, prev.sections.title.textColor),
                        },
                        subtitle: {
                            ...prev.sections.subtitle,
                            textColor: hex(s.COLOR_BODY_TEXT, prev.sections.subtitle.textColor),
                        },
                        text: {
                            ...prev.sections.text,
                            textColor: hex(s.COLOR_BODY_TEXT, prev.sections.text.textColor),
                        },
                        // Price now uses g.primary for the main number — only the
                        // discount/sales colour is editable per-section.
                        price: {
                            ...prev.sections.price,
                            discountColor: hex(s.COLOR_SALES_VALUE_PRIMARY, prev.sections.price.discountColor),
                        },
                        // Explorer-only: the "Groepen" sidebar's border colour.
                        // Not used on the product detail page in the real storefront,
                        // so we keep it scoped to this Explorer section instead of
                        // a global field.
                        sideFilter: {
                            ...prev.sections.sideFilter,
                            borderColor: hex(s.COLOR_BORDERS, prev.sections.sideFilter.borderColor),
                        },
                        footer: {
                            ...prev.sections.footer,
                            bgColor:   hex(s.COLOR_BACKGROUND_FOOTER, prev.sections.footer.bgColor),
                            textColor: hex(s.COLOR_TEXT_FOOTER,       prev.sections.footer.textColor),
                        },
                        footerExtra: {
                            ...prev.sections.footerExtra,
                            bgColor:   hex(s.COLOR_BACKGROUND_FOOTER_EXTRA, prev.sections.footerExtra.bgColor),
                            textColor: hex(s.COLOR_TEXT_FOOTER_EXTRA,       prev.sections.footerExtra.textColor),
                        },
                    },
                });
                    // Snapshot the loaded shape so the dirty check has a
                    // baseline. Mutating the ref inside the functional setState
                    // callback is safe — React guarantees the callback runs
                    // synchronously when the state update is dispatched.
                    originalStyleSettingsRef.current = next;
                    return next;
                });

                // ---- Product grid + variant grouping settings -----------
                // Maps the legacy mini-api setting keys to the modal's nested
                // config shape. `showCardOutline` has no backend key yet — it
                // stays at its default until the storefront supports it.
                // (readNum/readBool/readStr are module-scope helpers now.)
                // Pagination is stored as 'top' / 'bottom' / 'top-and-bottom'
                // on the backend; the modal uses 'above' / 'below' / 'both'.
                const paginationFromBackend = (v) =>
                    v === 'top' ? 'above' :
                    v === 'top-and-bottom' ? 'both' :
                    v === 'bottom' ? 'below' :
                    DEFAULT_PRODUCT_GRID_CONFIG.productGrid.paginationPosition;

                const loadedGrid = {
                    productGrid: {
                        maxColumns:         readNum (s.MAX_COLUMNS_EXPLORE,        DEFAULT_PRODUCT_GRID_CONFIG.productGrid.maxColumns),
                        paginationPosition: paginationFromBackend(s.EXPLORE_PAGINATION_POSITION),
                        showTotalProducts:  readBool(s.EXPLORE_SHOW_TOTALS,         DEFAULT_PRODUCT_GRID_CONFIG.productGrid.showTotalProducts),
                        showCardOutline:    DEFAULT_PRODUCT_GRID_CONFIG.productGrid.showCardOutline,
                    },
                    variantGrouping: {
                        groupAsOne:   readBool(s.EXPLORE_GROUP_VARIANTS,         DEFAULT_PRODUCT_GRID_CONFIG.variantGrouping.groupAsOne),
                        showSwatches: readBool(s.EXPLORE_SHOW_PRODUCT_VARIANTS,  DEFAULT_PRODUCT_GRID_CONFIG.variantGrouping.showSwatches),
                        maxVariants:  readNum (s.PRODUCT_SHOW_VARIANTS_AMOUNT,   DEFAULT_PRODUCT_GRID_CONFIG.variantGrouping.maxVariants),
                    },
                };
                setProductGridConfig(loadedGrid);
                originalProductGridConfigRef.current = loadedGrid;

                // ---- Product card config (Product information modal) ---
                // PRODUCT_DISPLAY_INFO stores three lines of CSV-of-keys
                // joined by ';' (e.g. "type,brand;reference;"). The modal
                // works with arrays of {id, key} chips — we round-trip on
                // save/load. Chip ids are regenerated each load; the dirty
                // check normalizes them away.
                let chipCounter = 0;
                const newId = (kind) => `${kind}-${Date.now()}-${++chipCounter}`;
                // ALL_FIELDS lives in ProductCardModal — duplicating just the
                // key set here avoids an import cycle.
                const KNOWN_KEYS = new Set([
                    'id','brand','name','reference','season','color','color_brand',
                    'description_1','description_2','description_3','description_4','description_5',
                    'category_1','category_2','category_3','category_4','category_5','category_6','category_7',
                    'detail_1','detail_2','detail_3','detail_4','detail_5',
                ]);
                const parseLines = (raw, fallback) => {
                    if (raw == null || raw === '') return fallback;
                    // PRODUCT_DISPLAY_INFO format: lines joined by ';'. Within a
                    // line, tokens are whitespace-separated; any non-recognised
                    // segment becomes a text chip so the user keeps their
                    // literal text (separators, parens, etc.) intact. The
                    // storefront's renderer does its own \b(known)\b regex
                    // replacement, so a text chip containing "(id)" still gets
                    // `id` swapped in on the actual product card.
                    const parts = String(raw).split(';');
                    return [0, 1, 2].map(i => {
                        const segment = parts[i] || '';
                        return segment
                            .split(/\s+/)
                            .filter(Boolean)
                            .map(tok => KNOWN_KEYS.has(tok)
                                ? { id: newId('chip'), key:  tok }
                                : { id: newId('txt'),  text: tok });
                    });
                };
                const fallbackLines = [
                    DEFAULT_PRODUCT_CARD_CONFIG.productInfo.line1.map(c => ({ ...c, id: newId('chip') })),
                    DEFAULT_PRODUCT_CARD_CONFIG.productInfo.line2.map(c => ({ ...c, id: newId('chip') })),
                    DEFAULT_PRODUCT_CARD_CONFIG.productInfo.line3.map(c => ({ ...c, id: newId('chip') })),
                ];
                const lines = s.PRODUCT_DISPLAY_INFO != null
                    ? parseLines(s.PRODUCT_DISPLAY_INFO, fallbackLines)
                    : fallbackLines;
                // EXPLORE_USE_IMAGE_FIT_RATIO is "W/H" (sometimes with spaces).
                // Default to whatever the modal's defaults are if unparseable.
                let aspW = DEFAULT_PRODUCT_CARD_CONFIG.image.aspectWidth;
                let aspH = DEFAULT_PRODUCT_CARD_CONFIG.image.aspectHeight;
                if (s.EXPLORE_USE_IMAGE_FIT_RATIO) {
                    const m = String(s.EXPLORE_USE_IMAGE_FIT_RATIO).match(/(\d+)\s*\/\s*(\d+)/);
                    if (m) { aspW = parseInt(m[1], 10); aspH = parseInt(m[2], 10); }
                }
                // The legacy admin only supports 'zoom' and 'second_image';
                // map second_image → fadeAlt for the modal's richer enum.
                const animTypeFromBackend = (v) =>
                    v === 'second_image' ? 'fadeAlt' :
                    v === 'zoom'         ? 'zoom'    :
                    DEFAULT_PRODUCT_CARD_CONFIG.image.animationType;

                const readColor = (v, fb) => (v == null || v === '' ? fb : String(v));

                const loadedCard = {
                    productInfo: {
                        line1: lines[0],
                        line2: lines[1],
                        line3: lines[2],
                        showDivider: readBool(s.PRODUCT_SHOW_DIVIDER,      DEFAULT_PRODUCT_CARD_CONFIG.productInfo.showDivider),
                        align:       readStr (s.PRODUCT_DETAILS_ALIGNMENT, DEFAULT_PRODUCT_CARD_CONFIG.productInfo.align),
                    },
                    image: {
                        useAlternativeAsMain: readBool(s.EXPLORE_USE_SECOND_IMAGE,      DEFAULT_PRODUCT_CARD_CONFIG.image.useAlternativeAsMain),
                        fillsCard:            readBool(s.EXPLORE_USE_IMAGE_FIT,         DEFAULT_PRODUCT_CARD_CONFIG.image.fillsCard),
                        aspectWidth:          aspW,
                        aspectHeight:         aspH,
                        cornerRounding:       readNum (s.PRODUCT_IMG_RADIUS,            DEFAULT_PRODUCT_CARD_CONFIG.image.cornerRounding),
                        animateOnHover:       readBool(s.ENABLE_PRODUCT_ITEM_ANIMATION, DEFAULT_PRODUCT_CARD_CONFIG.image.animateOnHover),
                        animationType:        animTypeFromBackend(s.PRODUCT_HOVER_ANIM_TYPE),
                    },
                    sizePreview: {
                        enabled:    readBool(s.PRODUCT_HOVER_SIZES,         DEFAULT_PRODUCT_CARD_CONFIG.sizePreview.enabled),
                        // PRODUCT_HOVER_MAX_SIZES comes back as a parsed array
                        // (mini-api explodes the CSV). Index [0] is the Y axis
                        // ("lengtematen" / number of sizes shown in the hover
                        // badge) — what the modal's "Maximum sizes shown"
                        // control edits. Index [1] (X / breedtematen / widths
                        // per size) is stashed in a ref so save preserves it.
                        maxShown:   (() => {
                            const arr = Array.isArray(s.PRODUCT_HOVER_MAX_SIZES) ? s.PRODUCT_HOVER_MAX_SIZES : [];
                            const y   = parseInt(arr[0], 10);
                            return Number.isFinite(y) ? y : DEFAULT_PRODUCT_CARD_CONFIG.sizePreview.maxShown;
                        })(),
                        position:   readStr (s.PRODUCT_SIZE_OVERLAY_POSITION, DEFAULT_PRODUCT_CARD_CONFIG.sizePreview.position),
                        badgeBg:    readColor(s.PRODUCT_SIZE_OVERLAY_BGC,     DEFAULT_PRODUCT_CARD_CONFIG.sizePreview.badgeBg),
                        badgeText:  readColor(s.PRODUCT_SIZE_OVERLAY_FGC,     DEFAULT_PRODUCT_CARD_CONFIG.sizePreview.badgeText),
                        textSize:   readNum (s.PRODUCT_SIZE_OVERLAY_SIZE,     DEFAULT_PRODUCT_CARD_CONFIG.sizePreview.textSize),
                        showBorder: readBool(s.PRODUCT_SIZE_SIZE_BORDER,      DEFAULT_PRODUCT_CARD_CONFIG.sizePreview.showBorder),
                    },
                };
                setProductCardConfig(loadedCard);
                originalProductCardConfigRef.current = loadedCard;
                // Stash original [1] ("breedtematen" / widths per size) so save
                // can rebuild the CSV without dropping it. Defaults to 5 to
                // match the install migration's '3,5' seed.
                {
                    const arr = Array.isArray(s.PRODUCT_HOVER_MAX_SIZES) ? s.PRODUCT_HOVER_MAX_SIZES : [];
                    const x   = parseInt(arr[1], 10);
                    productHoverMaxWidthsRef.current = Number.isFinite(x) ? x : 5;
                }

                // ---- Groups filter + sorting config -----------------------
                // The Filters & sortering modal maps to a handful of legacy
                // setting keys. CSV strings (orders) split on commas;
                // JSON-coerced settings (SIDE_FILTER_SLICE/STATES) come back
                // as objects already.
                const csvList = (raw) => String(raw == null ? '' : raw)
                    .split(',').map(s => s.trim()).filter(Boolean);
                // Modal field → legacy sort-rule field. Best-effort: gender
                // and type priorities are typically category_1/_2 on most
                // shops; createdAt has no legacy equivalent, fall back to id.
                const sortFieldFromBackend = (raw) => ({
                    price:           'price',
                    category_1_prio: 'genderPriority',
                    category_2_prio: 'typePriority',
                    id:              'id',
                    discount_perc:   'discount',
                    total_stock:     'stock',
                }[raw] || null);
                const parseSortRule = (raw, fallback) => {
                    if (raw == null || raw === '') return fallback;
                    const tokens = csvList(raw);
                    if (tokens.length === 0) return fallback;
                    let counter = 0;
                    const out = [];
                    for (const tok of tokens) {
                        const desc = tok.startsWith('-');
                        const name = (desc ? tok.slice(1) : tok).trim();
                        const mapped = sortFieldFromBackend(name);
                        if (mapped) out.push({ id: `r${++counter}`, field: mapped, direction: desc ? 'desc' : 'asc' });
                    }
                    return out.length ? out : fallback;
                };
                // Walk the loaded SIDE_FILTER_SLICE / SIDE_FILTER_STATES JSON
                // blobs into the modal's per-filter settings shape. Unknown
                // keys are ignored; missing keys fall back to the modal's
                // baseline (8 items, expanded).
                const sliceObj  = (typeof s.SIDE_FILTER_SLICE  === 'object' && s.SIDE_FILTER_SLICE)  ? s.SIDE_FILTER_SLICE  : {};
                const statesObj = (typeof s.SIDE_FILTER_STATES === 'object' && s.SIDE_FILTER_STATES) ? s.SIDE_FILTER_STATES : {};
                const activeFilters = s.SIDE_FILTER_ORDER ? csvList(s.SIDE_FILTER_ORDER) : DEFAULT_GROUPS_FILTER_CONFIG.activeFilters;
                const filterSettings = {};
                for (const fk of activeFilters) {
                    const fallback = DEFAULT_GROUPS_FILTER_CONFIG.filterSettings[fk] || { itemsBeforeMore: 8, expandedByDefault: true };
                    filterSettings[fk] = {
                        itemsBeforeMore: sliceObj[fk] != null && sliceObj[fk] !== '' ? Number(sliceObj[fk]) : fallback.itemsBeforeMore,
                        expandedByDefault: statesObj[fk] != null ? !!statesObj[fk] : fallback.expandedByDefault,
                    };
                }
                // Layout's `wherefilters` uses 'topbar'/'sidebar' in the modal;
                // legacy FILTER_POSITION uses 'top'/'side'.
                const wherefiltersFromBackend = (v) =>
                    v === 'top'  ? 'topbar'  :
                    v === 'side' ? 'sidebar' :
                    DEFAULT_GROUPS_FILTER_CONFIG.layout.wherefilters;
                // Modal: 'swatches'/'names'; legacy COLOR_FILTER_DISPLAY:
                // 'color'/'text' (default 'color' per mig032_20190412.php).
                const colorStyleFromBackend = (v) =>
                    v === 'color' ? 'swatches' :
                    v === 'text'  ? 'names'    :
                    DEFAULT_GROUPS_FILTER_CONFIG.layout.colorStyle;

                const loadedGroups = {
                    layout: {
                        wherefilters:     wherefiltersFromBackend(s.FILTER_POSITION),
                        sizeStyle:        readStr (s.FILTER_SIZE_STYLE,        DEFAULT_GROUPS_FILTER_CONFIG.layout.sizeStyle),
                        colorStyle:       colorStyleFromBackend(s.COLOR_FILTER_DISPLAY),
                        splitWidthLength: readBool(s.FILTER_TOP_SPLIT_SIZES,    DEFAULT_GROUPS_FILTER_CONFIG.layout.splitWidthLength),
                        showProductCount: readBool(s.SIDE_FILTER_SHOW_COUNTERS, DEFAULT_GROUPS_FILTER_CONFIG.layout.showProductCount),
                        hoverStyle:       readStr (s.FILTER_HOVER,              DEFAULT_GROUPS_FILTER_CONFIG.layout.hoverStyle),
                    },
                    activeFilters,
                    sortFiltersBy: readStr(s.ORDER_SIDE_FILTER_BY, DEFAULT_GROUPS_FILTER_CONFIG.sortFiltersBy),
                    filterSettings,
                    mobileHierarchy: s.FILTER_TREE_LEVELS ? csvList(s.FILTER_TREE_LEVELS) : DEFAULT_GROUPS_FILTER_CONFIG.mobileHierarchy,
                    sortOptions:     s.EXPLORE_SORT_ORDER ? csvList(s.EXPLORE_SORT_ORDER) : DEFAULT_GROUPS_FILTER_CONFIG.sortOptions,
                    defaultSortRule: parseSortRule(s.EXPLORE_SORT_DEFAULT_RULE, DEFAULT_GROUPS_FILTER_CONFIG.defaultSortRule),
                };
                setGroupsFilterConfig(loadedGroups);
                originalGroupsFilterConfigRef.current = loadedGroups;

                // ---- Global width settings -------------------------------
                // Maps the flat SHOP_MAX_WIDTH_* keys back into the modal's
                // nested {applyToWholeSite, productPagesWidth, perElement.*}
                // shape. Width keys are coerced to numbers by the mini-api
                // (now that they're in ADMIN_SETTINGS_INT_KEYS); FW_BG keys
                // are coerced to booleans.
                const widthRow = (widthKey, fwbgKey, fallback) => ({
                    width:       readNum (s[widthKey], fallback.width),
                    bgFullWidth: readBool(s[fwbgKey],  fallback.bgFullWidth ?? true),
                });
                const loadedWidths = {
                    applyToWholeSite:  readBool(s.SHOP_MAX_WIDTH_COMPLETE, false),
                    productPagesWidth: readNum (s.SHOP_MAX_WIDTH,          1500),
                    buttonBorderRadius: readNum(s.BUTTON_BORDER_RADIUS,    DEFAULT_GLOBAL_WIDTHS.buttonBorderRadius),
                    perElement: {
                        languageBar:     widthRow('SHOP_MAX_WIDTH_TOP',         'SHOP_MAX_WIDTH_TOP_FW_BG',         DEFAULT_GLOBAL_WIDTHS.perElement.languageBar),
                        header:          widthRow('SHOP_MAX_WIDTH_HEADER',      'SHOP_MAX_WIDTH_HEADER_FW_BG',      DEFAULT_GLOBAL_WIDTHS.perElement.header),
                        infoBannerBelow: widthRow('SHOP_MAX_WIDTH_BANNERS',     'SHOP_MAX_WIDTH_BANNERS_FW_BG',     DEFAULT_GLOBAL_WIDTHS.perElement.infoBannerBelow),
                        infoBannerTop:   widthRow('SHOP_MAX_WIDTH_BANNERS_TOP', 'SHOP_MAX_WIDTH_BANNERS_TOP_FW_BG', DEFAULT_GLOBAL_WIDTHS.perElement.infoBannerTop),
                        // brands & explore have no BG-stretch toggle in the modal
                        brands:          { width: readNum(s.SHOP_MAX_WIDTH_BRANDS,  DEFAULT_GLOBAL_WIDTHS.perElement.brands.width)  },
                        explore:         { width: readNum(s.SHOP_MAX_WIDTH_EXPLORE, DEFAULT_GLOBAL_WIDTHS.perElement.explore.width) },
                        recentlyViewed:  widthRow('SHOP_MAX_WIDTH_RECENT',      'SHOP_MAX_WIDTH_RECENT_FW_BG',      DEFAULT_GLOBAL_WIDTHS.perElement.recentlyViewed),
                        footer:          widthRow('SHOP_MAX_WIDTH_FOOTER',      'SHOP_MAX_WIDTH_FOOTER_FW_BG',      DEFAULT_GLOBAL_WIDTHS.perElement.footer),
                    },
                };
                setGlobalWidths(loadedWidths);
                originalGlobalWidthsRef.current = loadedWidths;

                // ---- Header / Logo (only height for now) ----------------
                // logoBarHeight ↔ HEADER_HEIGHT. The width/bgFullWidth fields
                // overlap with globalWidths.header and are not duplicated here.
                // Image URLs come from img_store_logo/header/icon.
                // display_name (the shop URL) frequently has NO protocol — see
                // Header.js, which prepends https:// the same way. Without it the
                // <img> src is a protocol-less RELATIVE url that 404s against the
                // admin domain (→ placeholder SVG, blank carousel/grid tiles).
                const headerImgBase = shopBase;
                const headerImgPrefix = (u) => (u ? `${headerImgBase}${u}` : '');
                const loadedHeaderLogo = {
                    size: {
                        ...DEFAULT_HEADER_LOGO_CONFIG.size,
                        logoBarHeight: readNum(s.HEADER_HEIGHT, DEFAULT_HEADER_LOGO_CONFIG.size.logoBarHeight),
                        // Max-width + bg stretch live in globalWidths.perElement.header
                        // (shared with GlobalWidthSettingsModal); the Header modal edits
                        // them straight through that state, not via headerLogoConfig.
                    },
                    // img_store_* are root-relative to the storefront; prefix the
                    // webshop base so the <img> previews resolve in the admin app.
                    images: {
                        logoUrl:       headerImgPrefix(s.img_store_logo),
                        backgroundUrl: headerImgPrefix(s.img_store_header),
                        faviconUrl:    headerImgPrefix(s.img_store_icon),
                    },
                };
                setHeaderLogoConfig(loadedHeaderLogo);
                originalHeaderLogoConfigRef.current = loadedHeaderLogo;

                // ---- Navigation (menu bar) config ----------------------
                // Maps headerContent toggles + outlet mode + a handful of
                // layout/menu fields back from their legacy SHOW_HEADER_* /
                // CENTER_* / HEADER_FILTER_* / HEADER_TYPE /
                // HEADER_ITEM_BREAK_AMOUNT / HOME_* keys.
                const navDefault = DEFAULT_NAVIGATION_CONFIG;
                const loadedNavigation = {
                    headerContent: {
                        contact:     readBool(s.SHOW_HEADER_ABOUTUS,  navDefault.headerContent.contact),
                        brands:      readBool(s.SHOW_HEADER_BRANDS,   navDefault.headerContent.brands),
                        shopTheLook: readBool(s.SHOW_HEADER_LOOKS,    navDefault.headerContent.shopTheLook),
                        outlet:      readBool(s.SHOW_SALES_BUTTON,    navDefault.headerContent.outlet),
                        giftVoucher: readBool(s.SHOW_HEADER_CHEQUES,  navDefault.headerContent.giftVoucher),
                        search:      readBool(s.SHOW_HEADER_SEARCH,   navDefault.headerContent.search),
                        account:     readBool(s.SHOW_HEADER_ACCOUNT,  navDefault.headerContent.account),
                        cart:        readBool(s.SHOW_HEADER_CART,     navDefault.headerContent.cart),
                        searchExpandedByDefault: readBool(s.DEFAULT_SEARCH_STATE_VISIBLE, navDefault.headerContent.searchExpandedByDefault),
                        showHomeButton:     readBool(s.HOME_ENABLED,  navDefault.headerContent.showHomeButton),
                        homeUrl:     s.HOME_URL || navDefault.headerContent.homeUrl,
                    },
                    outletMode: {
                        minDiscount:     readNum (s.MIN_DISCOUNT_FOR_SALES, navDefault.outletMode.minDiscount),
                        hideFromExplore: readBool(s.HIDE_SALES_FROM_EXPLORE, navDefault.outletMode.hideFromExplore),
                    },
                    layout: {
                        ...navDefault.layout,
                        menuStyle:               s.HEADER_TYPE              || navDefault.layout.menuStyle,
                        actionButtonsPosition:   s.HEADER_ACCOUNT_POSITION  || navDefault.layout.actionButtonsPosition,
                        centerTopHeaderButtons:  readBool(s.CENTER_HEADER,                navDefault.layout.centerTopHeaderButtons),
                        centerTopButtonInMiddle: readBool(s.CENTER_LOGO,                  navDefault.layout.centerTopButtonInMiddle),
                        alignActionButtonsRight: readBool(s.HEADER_FILTER_SPACE_ACTIONS, navDefault.layout.alignActionButtonsRight),
                    },
                    navigationMenu: {
                        ...navDefault.navigationMenu,
                        categoriesShown: s.NAVBAR_ITEMS_TO_LIST != null && s.NAVBAR_ITEMS_TO_LIST !== '0' && s.NAVBAR_ITEMS_TO_LIST !== ''
                            ? [String(s.NAVBAR_ITEMS_TO_LIST)]
                            : [],
                        itemsPerRow:  readNum(s.HEADER_ITEM_BREAK_AMOUNT, navDefault.navigationMenu.itemsPerRow),
                        menuHeight:   readNum(s.HEADER_FILTER_HEIGHT,    navDefault.navigationMenu.menuHeight),
                        menuTextSize: readNum(s.HEADER_FILTER_FONT_SIZE, navDefault.navigationMenu.menuTextSize),
                    },
                    behaviour: {
                        keepMenuVisibleWhileScrolling: readBool(s.SHOW_HEADER_FILTER_ON_SCROLL, navDefault.behaviour.keepMenuVisibleWhileScrolling),
                    },
                };
                setNavigationConfig(loadedNavigation);
                originalNavigationConfigRef.current = loadedNavigation;
                setNavFilterStyle(String(s.FILTER_STYLE || ''));

                // Navbar filter order — the only input it needs is the chosen
                // main-filter level (from the dump we already have). FIRE the
                // request now but don't await: awaiting here used to serialize
                // ~500 lines of unrelated parsing below behind one round-trip.
                // The await moved to the end of the parse body.
                const navOrderLevel = (loadedNavigation.navigationMenu.categoriesShown && loadedNavigation.navigationMenu.categoriesShown[0]) || '0';
                navbarOrderLevelRef.current = navOrderLevel;
                const navbarOrderPromise = navOrderLevel !== '0'
                    ? SftGlobalData.post('webshop&action=proxy', {
                        key:        props.api,
                        webshop_id: webshopId,
                        sftUserId:  props.login?.sft_user_id,
                        user:       props.login?.login,
                        method:     'GET',
                        path:       '/admin/navbar-order',
                        query:      { group: navOrderLevel },
                    }).catch(() => null)
                    : null;

                // Footer modal — newsletter (settings) + customLinks /
                // socialMedia (external_links rows split by `position`).
                // Width overlaps GlobalWidthSettingsModal (left as default).
                // Pages + contact still need separate endpoints (contents
                // table / lic_clients_webshop) and stay frontend-only.
                const footerDefault = DEFAULT_FOOTER_CONFIG;
                const allLinks = Array.isArray(s.externalLinks) ? s.externalLinks : [];
                // linkRowToFooter / linkRowToSocial now shared via configHelpers (F32).
                const loadedFooter = {
                    ...footerDefault,
                    newsletter: {
                        enabled:                readBool(s.SHOW_NEWSLETTER,               footerDefault.newsletter.enabled),
                        showInPopup:            readBool(s.NEWSLETTER_POPUP,              footerDefault.newsletter.showInPopup),
                        openPopupAutomatically: readBool(s.NEWSLETTER_POPUP_FIRST_VISIT,  footerDefault.newsletter.openPopupAutomatically),
                    },
                    // Per-page external-URL override. Empty value means
                    // "use the internal page" (TEXT_ABOUT etc.).
                    pages: {
                        about:      s.SF_LINK_ABOUT       || '',
                        terms:      s.SF_LINK_TERMS       || '',
                        privacy:    s.SF_LINK_PRIVACY     || '',
                        disclaimer: s.SF_LINK_DISCLAIMER  || '',
                    },
                    contact: {
                        shopName:    s.SF_INFO_NAME     || '',
                        addressLine: s.SF_INFO_ADDRESS  || '',
                        city:        s.SF_INFO_CITY     || '',
                        vatNumber:   s.SF_INFO_VAT      || '',
                        phone:       s.SF_INFO_PHONE    || '',
                        email:       s.SF_INFO_EMAIL    || '',
                        website:     s.SF_INFO_WEBSITE  || '',
                    },
                    customLinks: allLinks
                        .filter(r => String(r.position || '').toUpperCase() !== 'SOCIAL')
                        .map(linkRowToFooter),
                    socialMedia: allLinks
                        .filter(r => String(r.position || '').toUpperCase() === 'SOCIAL')
                        .map(linkRowToSocial),
                };
                setFooterConfig(loadedFooter);
                originalFooterConfigRef.current = loadedFooter;

                // Extra footer — payment toggle + per-column wysiwyg content.
                // Column content lives in the `contents` table (rows
                // TEXT_FOOTER_COL_{1,2,3}), exposed via the
                // /admin/settings dump as s.contents_footer_{1,2,3}.
                const extraFooterDefault = DEFAULT_EXTRA_FOOTER_CONFIG;
                const columnFromContents = contentsRowToColumn; // shared (F32)
                const loadedExtraFooter = {
                    ...extraFooterDefault,
                    showPaymentMethods: readBool(s.FOOTER_EXTRA_PAYMENT_METHODS, extraFooterDefault.showPaymentMethods),
                    columns: {
                        1: columnFromContents(s.contents_footer_1),
                        2: columnFromContents(s.contents_footer_2),
                        3: columnFromContents(s.contents_footer_3),
                    },
                };
                setExtraFooterConfig(loadedExtraFooter);
                originalExtraFooterConfigRef.current = loadedExtraFooter;

                // Static text pages (About / Disclaimer / Privacy) — each from
                // its `contents` row. Auto-pick the unlayer editor when a
                // per-language design exists on disk (mirrors Extra footer).
                const pageFromContents = contentsRowToPage; // shared (F32)
                const loadedStaticPages = {
                    about:      pageFromContents(s.contents_about),
                    disclaimer: pageFromContents(s.contents_disclaimer),
                    privacy:    pageFromContents(s.contents_privacy),
                    page404:    pageFromContents(s.contents_404),
                    intro:      pageFromContents(s.contents_intro),
                    detail:     pageFromContents(s.contents_detail),
                };
                setStaticPages(loadedStaticPages);
                originalStaticPagesRef.current = loadedStaticPages;

                // Image gallery — combines two settings rows + a JSON blob.
                // GALLERY_THUMBS_POSITION uses 'top'/'bottom'; the modal uses
                // 'above'/'below'. SHOW_GALLERY_THUMBS=false collapses the
                // position dropdown into 'hidden'.
                const imgGalDefault = DEFAULT_IMAGE_GALLERY_CONFIG;
                const legacyPos = (s.GALLERY_THUMBS_POSITION || 'left').toString().toLowerCase();
                const modalPos  = legacyPos === 'top' ? 'above'
                                : legacyPos === 'bottom' ? 'below'
                                : ['left','right','hidden'].includes(legacyPos) ? legacyPos
                                : imgGalDefault.thumbnailPosition;
                const thumbsOn  = readBool(s.SHOW_GALLERY_THUMBS, true);
                const ngc = s.NEW_GALLERY_CONFIG && typeof s.NEW_GALLERY_CONFIG === 'object'
                    ? s.NEW_GALLERY_CONFIG
                    : {};
                const mainArrows  = ngc.main_arrows  || {};
                const thumbArrows = ngc.thumb_arrows || {};
                const loadedImgGal = {
                    thumbnailPosition:      thumbsOn ? modalPos : 'hidden',
                    useGrid:                readBool(s.DETAIL_USE_NEW_GALLERY, imgGalDefault.useGrid),
                    maxThumbnails:          readNum(s.NEW_GALLERY_THUMBS_COUNT, imgGalDefault.maxThumbnails),
                    showArrows:             mainArrows.display !== undefined ? !!mainArrows.display : imgGalDefault.showArrows,
                    arrowColor:             mainArrows.color      || imgGalDefault.arrowColor,
                    arrowBgColor:           mainArrows.background || imgGalDefault.arrowBgColor,
                    showFullscreen:         thumbArrows.display !== undefined ? !!thumbArrows.display : imgGalDefault.showFullscreen,
                    fullscreenArrowColor:   thumbArrows.color      || imgGalDefault.fullscreenArrowColor,
                    fullscreenArrowBgColor: thumbArrows.background || imgGalDefault.fullscreenArrowBgColor,
                };
                setImageGalleryConfig(loadedImgGal);
                originalImageGalleryConfigRef.current = loadedImgGal;

                // Product Detail Information — seven of the eight cards
                // round-trip to legacy settings (layout, sectionsOpen,
                // detailsList, related, stock, titleAndUrl, breadcrumb).
                // Only `descriptionTexts` (per-language placement of
                // description fields) has no backend mapping yet — its
                // card surfaces a "planned" banner and edits stay local
                // until the storefront supports per-language toggles.
                const pdiDefault = DEFAULT_PRODUCT_DETAIL_INFO_CONFIG;

                // Layout — PRODUCT_DETAILS_ORDER is a CSV of legacy block
                // keys. Blocks not in the CSV are hidden on the storefront
                // (its component sets order=null and *ngIfs the section
                // away). Map legacy ↔ modal both ways.
                const legacyToModalBlock = {
                    header:          'header',
                    sizeSelector:    'sizeSelector',
                    addToCart:       'addToCart',
                    locationsStock:  'stockByLocation',
                    variantsList:    'variants',
                    related:         'relatedProducts',
                    productInfo:     'productInfo',
                    extraText:       'extraText',
                };
                const orderCsv     = String(s.PRODUCT_DETAILS_ORDER || '').trim();
                const legacyOrder  = orderCsv ? orderCsv.split(',').map(k => k.trim()).filter(Boolean) : [];
                const modalOrder   = legacyOrder.map(k => legacyToModalBlock[k]).filter(Boolean);
                const usedSet      = new Set(modalOrder);
                const loadedBlocks = Object.fromEntries(
                    Object.keys(pdiDefault.layout.blocks).map(k => [k, { used: usedSet.has(k) }])
                );

                // Container states (JSON-coerced). Modal sections map to
                // legacy keys via a short table:
                //   productDetails ↔ details, variants ↔ variants,
                //   relatedProducts ↔ related, articleInfo ↔ information,
                //   washInstructions ↔ washAndCare, stockByStore ↔ storeStock
                const sectionsMap = {
                    productDetails:   'details',
                    variants:         'variants',
                    relatedProducts:  'related',
                    articleInfo:      'information',
                    washInstructions: 'washAndCare',
                    stockByStore:     'storeStock',
                };
                const readStates = (raw, fallback) => {
                    const obj = (raw && typeof raw === 'object') ? raw : {};
                    return Object.fromEntries(
                        Object.entries(sectionsMap).map(([modalK, legacyK]) =>
                            [modalK, obj[legacyK] !== undefined ? !!obj[legacyK] : !!fallback[modalK]]
                        )
                    );
                };

                // Variants display: legacy DETAILS_COLOR_DISPLAY only knows
                // 'color' (chips) and 'image'. The modal extends with 3 more
                // values, so round-trip raw and only translate the legacy pair.
                const legacyToModalVariant = (v) => v === 'image' ? 'colorImage'
                                              : v === 'color' ? 'colorChips'
                                              : (v || pdiDefault.related.variantsDisplay);

                // Details list — SHOW_FIELDS_ON_DETAIL_PAGE is a flat CSV
                // of legacy keys. Whichever group a key lives in modal-side,
                // the underlying CSV stays flat; load splits the CSV into
                // the same three groups by key.
                const fieldsCsv = String(s.SHOW_FIELDS_ON_DETAIL_PAGE || '').trim();
                const fieldsSet = new Set(
                    fieldsCsv ? fieldsCsv.split(',').map(k => k.trim()).filter(Boolean) : []
                );
                const readGroup = (groupDefault) => Object.fromEntries(
                    Object.keys(groupDefault).map(k => [k, fieldsSet.has(k)])
                );

                // Per-language product-text types: PRODUCT_TEXTS_LOCALE and
                // PRODUCT_TEXTS_META_LOCALE are ';'-joined nl;en;fr slots
                // (comma-lists of POS text-type codes; empty = none). The
                // storefront reads them in Product.php getById.
                const pageTypes = String(s.PRODUCT_TEXTS_LOCALE      ?? '').split(';');
                const metaTypes = String(s.PRODUCT_TEXTS_META_LOCALE ?? '').split(';');
                const descTexts = {
                    nl: { page: (pageTypes[0] || '').trim(), meta: (metaTypes[0] || '').trim() },
                    en: { page: (pageTypes[1] || '').trim(), meta: (metaTypes[1] || '').trim() },
                    fr: { page: (pageTypes[2] || '').trim(), meta: (metaTypes[2] || '').trim() },
                };

                const loadedPdi = {
                    ...pdiDefault,
                    descriptionTexts: descTexts,
                    layout: {
                        blocks: loadedBlocks,
                        order:  modalOrder.length ? modalOrder : pdiDefault.layout.order,
                    },
                    detailsList: fieldsCsv ? {
                        identity:     readGroup(pdiDefault.detailsList.identity),
                        categories:   readGroup(pdiDefault.detailsList.categories),
                        descriptions: readGroup(pdiDefault.detailsList.descriptions),
                    } : pdiDefault.detailsList,
                    sectionsOpen: {
                        desktop: readStates(s.PRODUCT_DETAILS_CONTAINER_STATES, pdiDefault.sectionsOpen.desktop),
                        mobile:  readStates(s.PRODUCT_MOBILE_CONTAINER_STATES,  pdiDefault.sectionsOpen.mobile),
                    },
                    related: {
                        maxToShow:         readNum (s.PRODUCT_RELATED_ARTICLES_AMOUNT,    pdiDefault.related.maxToShow),
                        variantsDisplay:   legacyToModalVariant(s.DETAILS_COLOR_DISPLAY),
                        showOutOfStock:    readBool(s.DETAILS_COLOR_SHOW_OUT_OF_STOCK,    pdiDefault.related.showOutOfStock),
                        highlightSelected: readBool(s.DETAILS_COLOR_DISPLAY_BORDER,       pdiDefault.related.highlightSelected),
                    },
                    stock: {
                        showStockByStore:    readBool(s.SHOW_AVAILABLE_STOCK_STORES, pdiDefault.stock.showStockByStore),
                        hideWhenSizeUnknown: readBool(s.HIDE_STORE_IF_NO_RECORD,     pdiDefault.stock.hideWhenSizeUnknown),
                    },
                    // Title & URL slots — three free-form templates parse
                    // into mixed token+text chip arrays; URL slug is CSV-
                    // only and only carries token chips.
                    titleAndUrl: {
                        browserTab:     { used: parseTemplateToChips(s.SHOW_FIELDS_ON_DETAIL_TAB) },
                        pageHeading:    { used: parseTemplateToChips(s.SHOW_FIELDS_ON_DETAIL_TITLE) },
                        pageSubheading: { used: parseTemplateToChips(s.SHOW_FIELDS_ON_DETAIL_SUBHEADING) },
                        urlSlug:        { used: parseCsvToChips    (s.DETAIL_PAGE_URL_SECTION) },
                    },
                    // Breadcrumb — CSV of legacy field keys from
                    // DETAIL_PAGE_NAV_SECTION. Unknown keys are dropped.
                    breadcrumb: {
                        used: String(s.DETAIL_PAGE_NAV_SECTION || '')
                            .split(',').map(k => k.trim()).filter(Boolean),
                    },
                };
                setProductDetailInfoConfig(loadedPdi);
                originalProductDetailInfoConfigRef.current = loadedPdi;

                // Carousel slides — `s.carousel` already lives in the
                // /admin/settings dump (one row per slide, with `lang` for
                // language scoping). Group by lang and translate the row
                // shape to what the modal expects. `serverId` tracks the
                // backend row id so save can diff which slides need POST/
                // PATCH/DELETE.
                //
                // Image URLs: the mini-api hands back a root-relative
                // `carouselImageUrl` (e.g. `/docroot/img-repo/home-gallery/`)
                // which works on the storefront's own domain but not in this
                // admin app. We prefix it with the webshop base (stripped of
                // the `/server/v1/mini-api` suffix) so <img> tags resolve.
                const carouselRows = Array.isArray(s.carousel) ? s.carousel : [];
                // Same protocol-normalisation as the header logo (display_name
                // often lacks https://) so carousel/grid image previews resolve.
                const webshopBase  = shopBase;
                const carouselUrlBase = s.carouselImageUrl || '';
                carouselImgBaseRef.current = `${webshopBase}${carouselUrlBase}`;
                // Shared mapper (F32) — same one the post-save refetch uses.
                const carImgBase = `${webshopBase}${carouselUrlBase}`;
                const byLang = (lang) => rowsByLang(carouselRows, lang, (r, i) => slideRowToForm(r, i, carImgBase));
                // Carousel settings — PER LANGUAGE since 2026-06-04. Each
                // language has its own HOME_GALLERY_CONFIG_{L} JSON +
                // STRETCH_/GALLERY_HOVER_ scalar keys +
                // HOME_GALLERY_OVERLAY_CONFIG_{L}; the modal's language pills
                // now apply to the Settings tab too. `settings` is keyed
                // { nl, en, fr }.
                const carouselDefault = DEFAULT_CAROUSEL_CONFIG.settings.nl;
                // Position is space-separated on the backend ('bottom center')
                // vs hyphen in the modal; 'center' (no axis) is the legacy
                // shorthand for 'middle center'.
                const carPositionFromBackend = (p, fb) => {
                    if (typeof p !== 'string' || !p) return fb;
                    if (p === 'center') return 'middle-center';
                    if (p.includes(' ')) return p.replace(/\s+/g, '-');
                    return p;
                };
                const carReadDevice = (raw, fb) => {
                    const o = raw || {};
                    return {
                        background: o.background ?? fb.background,
                        textColor:  o.color      ?? fb.textColor,
                        position:   carPositionFromBackend(o.position, fb.position),
                        // backend fontSize = font size; backend size = overlay band height %.
                        size:       o.fontSize ?? fb.size,
                        width:      o.size     ?? fb.width,
                        bold:       !!o.styleBold,
                        italic:     !!o.styleItalic,
                        underline:  !!o.styleLined,
                        strike:     !!o.styleStrike,
                    };
                };
                // L = 'NL' | 'EN' | 'FR' (settings-key suffix).
                const parseCarouselLang = (L) => {
                    const galleryConfig = (s[`HOME_GALLERY_CONFIG_${L}`] && typeof s[`HOME_GALLERY_CONFIG_${L}`] === 'object')
                        ? s[`HOME_GALLERY_CONFIG_${L}`]
                        : {};
                    // The storefront reads the caption overlay from the SEPARATE
                    // HOME_GALLERY_OVERLAY_CONFIG_{L} key (StartupController merges
                    // it in as `overlayConfig`); fall back to the legacy inline spot.
                    const overlayRaw = (s[`HOME_GALLERY_OVERLAY_CONFIG_${L}`] && typeof s[`HOME_GALLERY_OVERLAY_CONFIG_${L}`] === 'object')
                        ? s[`HOME_GALLERY_OVERLAY_CONFIG_${L}`]
                        : (galleryConfig.overlayConfig || {});
                    const deskRaw = overlayRaw.desktop || {};
                    // margin = [top, bottom] (mig164).
                    const margin = Array.isArray(galleryConfig.margin) ? galleryConfig.margin : null;
                    return {
                        // Raw JSONs kept per language so save can merge tier-1
                        // values without dropping unknown legacy fields.
                        __rawGalleryConfig: galleryConfig,
                        __rawOverlayConfig: overlayRaw,
                        display: {
                            ...carouselDefault.display,
                            fullWidth:     readBool(s[`STRETCH_GALLERY_FULL_WIDTH_${L}`], carouselDefault.display.fullWidth),
                            maxHeight:     galleryConfig.maxHeight != null ? Number(galleryConfig.maxHeight) : carouselDefault.display.maxHeight,
                            maxHeightUnit: galleryConfig.maxHeightUnit || carouselDefault.display.maxHeightUnit,
                            spacingTop:    margin ? (Number(margin[0]) || 0) : carouselDefault.display.spacingTop,
                            spacingBottom: margin ? (Number(margin[1]) || 0) : carouselDefault.display.spacingBottom,
                            imageFit:      galleryConfig.imageSize || carouselDefault.display.imageFit,
                        },
                        hover: {
                            ...carouselDefault.hover,
                            effect:         s[`GALLERY_HOVER_ANIM_TYPE_${L}`] || carouselDefault.hover.effect,
                            zoomLevel:      readNum(s[`GALLERY_HOVER_ANIM_ZOOM_SCALE_${L}`], carouselDefault.hover.zoomLevel),
                            animationSpeed: s[`GALLERY_HOVER_ANIM_ZOOM_SPEED_${L}`] != null
                                ? Number(s[`GALLERY_HOVER_ANIM_ZOOM_SPEED_${L}`])
                                : carouselDefault.hover.animationSpeed,
                        },
                        behavior: {
                            ...carouselDefault.behavior,
                            showArrows:       galleryConfig.showControls           != null ? !!galleryConfig.showControls           : carouselDefault.behavior.showArrows,
                            arrowsOnHover:    galleryConfig.showControlsOnHover    != null ? !!galleryConfig.showControlsOnHover    : carouselDefault.behavior.arrowsOnHover,
                            arrowsBackground: galleryConfig.showControlsBackground != null ? !!galleryConfig.showControlsBackground : carouselDefault.behavior.arrowsBackground,
                            autoAdvance:      galleryConfig.autoSlide              != null ? !!galleryConfig.autoSlide              : carouselDefault.behavior.autoAdvance,
                            // Interval is in ms backend-side, seconds in the modal.
                            // Per-lang slideInterval (storefront reads the per-lang
                            // JSON) with the legacy global key as fallback.
                            timePerSlide:     (galleryConfig.slideInterval != null
                                ? Number(galleryConfig.slideInterval)
                                : readNum(s.HOME_GALLERY_INTERVAL, carouselDefault.behavior.timePerSlide * 1000)) / 1000,
                            transition:       galleryConfig.animation || carouselDefault.behavior.transition,
                        },
                        textOverlay: {
                            desktop: carReadDevice(deskRaw,           carouselDefault.textOverlay.desktop),
                            mobile:  carReadDevice(overlayRaw.mobile, carouselDefault.textOverlay.mobile),
                            onlyOnHover:        !!deskRaw.onlyOnHover,
                            alwaysVisible:      !!deskRaw.keepTextDisplayed,
                            useDesktopOnMobile: !!deskRaw.useDesktopTextFallback,
                        },
                    };
                };
                const loadedCarousel = {
                    ...DEFAULT_CAROUSEL_CONFIG,
                    slides:   { nl: byLang('nl'), en: byLang('en'), fr: byLang('fr') },
                    settings: (() => {
                        // F22: parse the all-defaults result ONCE and share it
                        // between languages that have no data at all — same
                        // shape as the real parse (so save merge-bases stay
                        // intact), without building 3 full config graphs for
                        // an empty/failed collections bundle.
                        const hasData = (L) =>
                            (s[`HOME_GALLERY_CONFIG_${L}`] && typeof s[`HOME_GALLERY_CONFIG_${L}`] === 'object')
                            || (s[`HOME_GALLERY_OVERLAY_CONFIG_${L}`] && typeof s[`HOME_GALLERY_OVERLAY_CONFIG_${L}`] === 'object')
                            || s[`STRETCH_GALLERY_FULL_WIDTH_${L}`] != null
                            || s[`GALLERY_HOVER_ANIM_TYPE_${L}`] != null
                            || s.HOME_GALLERY_INTERVAL != null;
                        let empty = null;
                        const forLang = (L) => {
                            if (hasData(L)) return parseCarouselLang(L);
                            if (!empty) empty = parseCarouselLang(L); // pure defaults, L-independent
                            return empty;
                        };
                        return { nl: forLang('NL'), en: forLang('EN'), fr: forLang('FR') };
                    })(),
                };
                setCarouselConfig(loadedCarousel);
                originalCarouselConfigRef.current = loadedCarousel;

                // Image grid cells — same shape as carousel, different
                // image directory (`image-grid/` instead of `home-gallery/`).
                // The mini-api doesn't expose a separate `imageGridImageUrl`,
                // so we compose `${webshopBase}/${img_path}/image-grid/` from
                // the same pieces.
                const gridRows = Array.isArray(s.imageGrid) ? s.imageGrid : [];
                const gridUrlBase = s.imageGridImageUrl || `${s.img_path || ''}/image-grid/`;
                gridImgBaseRef.current = `${webshopBase}${gridUrlBase}`;
                // Shared mapper (F32) — same one the post-save refetch uses.
                const gridImgBaseFull = `${webshopBase}${gridUrlBase}`;
                const gridByLang = (lang) => rowsByLang(gridRows, lang, (r, i) => gridCellRowToForm(r, i, gridImgBaseFull));
                // Image grid settings — same canonical-NL approach as
                // carousel. Layout split between scalar per-lang keys
                // (IMAGE_GRID_COLS_NL, IMAGE_GRID_GUTTER_NL) and a JSON
                // blob (IMAGE_GRID_CONFIG_NL).
                const gridDefault    = DEFAULT_IMAGE_GRID_CONFIG.settings.nl;
                // Overlay caption — mig166 added `overlay.desktop` /
                // `overlay.mobile` sub-objects to IMAGE_GRID_CONFIG_{lang}.
                // Modal has matching dual editors (same shape as carousel).
                // Position uses space-separated tokens on the backend
                // ('bottom center', 'middle center', …) vs hyphen in the
                // modal ('bottom-center', 'middle-center', …) — storefront's
                // home.component.html does `.includes('top ')` matching, so
                // the space format is mandatory on save. A bare 'center'
                // (no axis) was a pre-mig166 shorthand for 'middle center';
                // alias it so old configs land on the correct dropdown option.
                const positionFromBackend = (p, fb) => {
                    if (typeof p !== 'string' || !p) return fb;
                    if (p === 'center') return 'middle-center';
                    if (p.includes(' ')) return p.replace(/\s+/g, '-');
                    return p;
                };
                const readDevice = (raw, fb) => {
                    const o = raw || {};
                    return {
                        background: o.background ?? fb.background,
                        textColor:  o.color      ?? fb.textColor,
                        position:   positionFromBackend(o.position, fb.position),
                        // Backend `fontSize` = font size; backend `size` = overlay band
                        // height %. Modal calls those `size` and `width` respectively.
                        size:       o.fontSize ?? fb.size,
                        width:      o.size     ?? fb.width,
                        bold:       !!o.styleBold,
                        italic:     !!o.styleItalic,
                        underline:  !!o.styleLined,
                        strike:     !!o.styleStrike,
                    };
                };
                // PER LANGUAGE since 2026-06-04 — every key already had per-lang
                // variants (IMAGE_GRID_COLS_{L}, GRID_HOVER_*_{L}, KEEP_GRID_{L},
                // IMAGE_GRID_CONFIG_{L}); we now read/write each independently.
                const parseGridLang = (L) => {
                    const gridJson = (s[`IMAGE_GRID_CONFIG_${L}`] && typeof s[`IMAGE_GRID_CONFIG_${L}`] === 'object')
                        ? s[`IMAGE_GRID_CONFIG_${L}`]
                        : {};
                    const rawOverlay = gridJson.overlay || {};
                    const desk       = rawOverlay.desktop || {};
                    // Margin: IMAGE_GRID_CONFIG_{lang}.margin = [top, bottom] (mig164).
                    const marginArr  = Array.isArray(gridJson.margin)
                        ? gridJson.margin
                        : gridDefault.layout.margin;
                    return {
                        __rawGridConfig: gridJson,
                        layout: {
                            ...gridDefault.layout,
                            columns:          readNum(s[`IMAGE_GRID_COLS_${L}`],   gridDefault.layout.columns),
                            gap:              readNum(s[`IMAGE_GRID_GUTTER_${L}`], gridDefault.layout.gap),
                            // KEEP_GRID_ON_MOBILE is per-lang (mig158). Default true
                            // per mig112 ('true' string). readBool handles 'true'/1/'1'.
                            keepGridOnMobile: readBool(s[`IMAGE_GRID_KEEP_GRID_ON_MOBILE_${L}`], gridDefault.layout.keepGridOnMobile),
                            margin:           [Number(marginArr[0]) || 0, Number(marginArr[1]) || 0],
                        },
                        hover: {
                            ...gridDefault.hover,
                            effect:         s[`GRID_HOVER_ANIM_TYPE_${L}`] || gridDefault.hover.effect,
                            zoomLevel:      readNum(s[`GRID_HOVER_ANIM_ZOOM_SCALE_${L}`], gridDefault.hover.zoomLevel),
                            animationSpeed: s[`GRID_HOVER_ANIM_ZOOM_SPEED_${L}`] != null
                                ? Number(s[`GRID_HOVER_ANIM_ZOOM_SPEED_${L}`])
                                : gridDefault.hover.animationSpeed,
                        },
                        textOverlay: {
                            desktop: readDevice(desk,              gridDefault.textOverlay.desktop),
                            mobile:  readDevice(rawOverlay.mobile, gridDefault.textOverlay.mobile),
                            // Shared flags only live on the desktop sub-object
                            // backend-side; modal exposes them as top-level.
                            onlyOnHover:        !!desk.onlyOnHover,
                            alwaysVisible:      !!desk.keepTextDisplayed,
                            useDesktopOnMobile: !!desk.useDesktopTextFallback,
                        },
                    };
                };
                const loadedImageGrid = {
                    ...DEFAULT_IMAGE_GRID_CONFIG,
                    cells:    { nl: gridByLang('nl'), en: gridByLang('en'), fr: gridByLang('fr') },
                    settings: (() => {
                        // F22 — same shared-empty trick as the carousel above.
                        const hasData = (L) =>
                            (s[`IMAGE_GRID_CONFIG_${L}`] && typeof s[`IMAGE_GRID_CONFIG_${L}`] === 'object')
                            || s[`IMAGE_GRID_COLS_${L}`] != null
                            || s[`IMAGE_GRID_GUTTER_${L}`] != null
                            || s[`GRID_HOVER_ANIM_TYPE_${L}`] != null;
                        let empty = null;
                        const forLang = (L) => {
                            if (hasData(L)) return parseGridLang(L);
                            if (!empty) empty = parseGridLang(L);
                            return empty;
                        };
                        return { nl: forLang('NL'), en: forLang('EN'), fr: forLang('FR') };
                    })(),
                };
                setImageGridConfig(loadedImageGrid);
                originalImageGridConfigRef.current = loadedImageGrid;

                // Navbar order — awaited last (see the fire above): all the
                // parsing in between never depended on it.
                if (navbarOrderPromise) {
                    const noRes  = await navbarOrderPromise;
                    const noRows = (noRes?.success && Array.isArray(noRes.data)) ? noRes.data : [];
                    setNavbarOrder(noRows);
                    originalNavbarOrderRef.current = noRows;
                } else {
                    setNavbarOrder([]);
                    originalNavbarOrderRef.current = [];
                }
            } catch (e) {
                if (!cancelled) setConfigError({ message: e?.message || 'Network error', code: 'error_network' });
            } finally {
                if (!cancelled) setConfigLoading(false);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
};
